import * as React from 'react';
import { clsx } from 'clsx';
import { Check, Minus } from 'lucide-react';

export interface CheckboxProps {
  /**
   * The label for the checkbox
   */
  label?: string;
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  /**
   * Callback when checked state changes
   */
  onChange?: (checked: boolean) => void;
  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
  /**
   * Name attribute for form submission
   */
  name?: string;
  /**
   * Value attribute for form submission
   */
  value?: string;
  /**
   * Indeterminate state (shows minus icon)
   */
  indeterminate?: boolean;
}

const checkboxStyles: React.CSSProperties = {
  width: '16px',
  height: '16px',
  border: '1px solid #7e7e7e', // grey-500
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  flexShrink: 0,
  backgroundColor: '#ffffff',
};

const checkedStyles: React.CSSProperties = {
  backgroundColor: '#0e8a0e', // brand-600
  border: '1px solid #0e8a0e',
};

const checkedHoverStyles: React.CSSProperties = {
  backgroundColor: '#005700', // brand-800
  border: '1px solid #005700',
};

const uncheckedHoverStyles: React.CSSProperties = {
  border: '1px solid #474747', // grey-700
};

const labelStyles: React.CSSProperties = {
  fontSize: '13px',
  color: '#2f2f2f',
  cursor: 'pointer',
  userSelect: 'none' as const,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const wrapperStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

/**
 * Checkbox component - Arbor Design System
 *
 * A checkbox input with label support.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      checked = false,
      onChange,
      disabled = false,
      className,
      style,
      'data-testid': dataTestId,
      name,
      value,
      indeterminate = false,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const checkboxId = React.useId();
    const innerRef = React.useRef<HTMLInputElement | null>(null);

    // Merge refs
    React.useEffect(() => {
      const node = innerRef.current;
      if (node) {
        // Set indeterminate property on the native input element
        node.indeterminate = indeterminate;

        // Forward to external ref if provided
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      }
    }, [indeterminate, ref]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(e.target.checked);
      }
    };

    const boxStyle: React.CSSProperties = {
      ...checkboxStyles,
      ...((checked || indeterminate) && !disabled && checkedStyles),
      ...(isHovered && !disabled && !(checked || indeterminate) && uncheckedHoverStyles),
      ...(isHovered && !disabled && (checked || indeterminate) && checkedHoverStyles),
      ...(disabled && !checked && !indeterminate && { backgroundColor: '#efefef', border: '1px solid #b3b3b3' }),
      ...(disabled && (checked || indeterminate) && { backgroundColor: '#b3b3b3', border: '1px solid #b3b3b3' }),
      ...(isFocused && !disabled && {
        boxShadow: '0px 0px 0px 3px #3cad51',
      }),
    };

    const checkmarkColor = '#ffffff';

    return (
      <div
        className={clsx('arbor-checkbox-wrapper', className)}
        style={{ ...wrapperStyles, ...style }}
        data-testid={dataTestId}
      >
        <input
          ref={innerRef}
          id={checkboxId}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          name={name}
          value={value}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 0,
            height: 0,
          }}
          aria-checked={indeterminate ? 'mixed' : checked}
        />
        <label
          htmlFor={checkboxId}
          onMouseEnter={() => !disabled && setIsHovered(true)}
          onMouseLeave={() => !disabled && setIsHovered(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <div style={boxStyle}>
            {(checked || indeterminate) && (
              indeterminate ? (
                <Minus size={12} color={checkmarkColor} strokeWidth={3} />
              ) : (
                <Check size={16} color={checkmarkColor} strokeWidth={3} />
              )
            )}
          </div>
          {label && (
            <span
              style={{
                ...labelStyles,
                color: disabled ? '#7e7e7e' : '#2f2f2f',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {label}
            </span>
          )}
        </label>
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
